import classes from 'classnames';
import React, { memo, useCallback, useRef, useState } from 'react';
import Lottie from 'react-lottie';
import CheckIcon from '@/assets/icons/check-circle.svg?react';
import ErrorIcon from '@/assets/icons/error-circle.svg?react';
import EyeClosedIcon from '@/assets/icons/eye-closed.svg?react';
import EyeOpenIcon from '@/assets/icons/eye-open.svg?react';
import MagnifyingGlassIcon from '@/assets/icons/magnifying-glass.svg?react';
import LoadingAnimation from '@/assets/lottie/loading.json';
import ErrorMessage from '@/components/ErrorMessage/ErrorMessage';
import styles from './Input.module.scss';
import useDebounce from '@/hooks/useDebounce';
import { EventCollectorProps } from '@/hooks/context/EventCollector/types';
import TextInputEvent from '@/hooks/context/EventCollector/events/TextInputEvent';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';

export type Props = {
  type?: 'text' | 'password' | 'tel' | 'email' | 'number';
  value?: string | number;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string | boolean;
  errorClassName?: string;
  isTouched?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  postFix?: string;
  showPasswordReveal?: boolean;
  dataTest?: string;
  stackable?: boolean;
  className?: string;
  classNameInput?: string;
  autoComplete?: 'on' | 'off';
  withSearchIcon?: boolean;
  disabled?: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export default memo(function Input({
  type = 'text',
  onChange,
  value = '',
  name,
  onBlur,
  onClick,
  error,
  isTouched,
  isLoading = false,
  placeholder,
  postFix,
  showPasswordReveal = false,
  dataTest = 'input',
  stackable = false,
  className,
  classNameInput,
  autoComplete,
  withSearchIcon = false,
  eventCollector,
  errorClassName,
  disabled,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();
  const valueRef = useRef(value);
  const [passwordVisibility, setPasswordVisibility] = useState<
    'password' | 'text'
  >('password');

  const handlePasswordToggle = useCallback(() => {
    setPasswordVisibility(
      passwordVisibility === 'password' ? 'text' : 'password',
    );
  }, [passwordVisibility]);

  const handleEventCollector = useCallback(
    (val: string | number) => {
      if (!isTouched || eventCollector?.dontTrack || valueRef.current === val) {
        return;
      }

      track(
        new TextInputEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? val.toString(),
          description: eventCollector?.description ?? name ?? '',
        }),
      );

      valueRef.current = val;
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      isTouched,
      name,
      track,
    ],
  );

  useDebounce(() => handleEventCollector(value), 1000, [value]);

  const showError = isTouched && error;
  const showCheck = isTouched && !error;
  const EyeIcon =
    passwordVisibility === 'password' ? EyeClosedIcon : EyeOpenIcon;

  const loadingLottieOptions = {
    loop: true,
    autoplay: true,
    animationData: LoadingAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const loadingAnimation = isLoading && (
    <div className={styles['loading-animation']}>
      <Lottie
        width={80}
        height={80}
        options={loadingLottieOptions}
        isClickToPauseDisabled
      />
    </div>
  );

  return (
    <div className={classes(styles.container, className)}>
      <div
        className={classes(
          styles.field,
          stackable && styles.stackable,
          showError && styles['field-error'],
        )}
      >
        <input
          autoComplete={autoComplete}
          data-test={dataTest}
          className={classes(
            styles.input,
            showError && styles['input-error'],
            classNameInput,
          )}
          name={name}
          id={name}
          type={type === 'password' ? passwordVisibility : type}
          onChange={onChange}
          value={value}
          onBlur={onBlur}
          onClick={onClick}
          disabled={disabled}
        />
        <label
          htmlFor={name}
          className={classes(
            styles.label,
            showError && styles['label-error'],
            value?.toString().length && styles.float,
          )}
          data-test={`${dataTest}-placeholder`}
        >
          {placeholder}
        </label>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div
          className={styles.badges}
          onClick={showPasswordReveal ? handlePasswordToggle : undefined}
        >
          {showPasswordReveal && (
            <EyeIcon
              className={classes(styles.badge, styles['password-icon'])}
              data-test={`${dataTest}-eye-icon`}
            />
          )}
          {postFix && (
            <span
              className={classes(
                styles.badge,
                styles['post-fix'],
                showError && styles['post-fix-error'],
              )}
              data-test={`${dataTest}-postfix`}
            >
              {postFix}
            </span>
          )}
          {(showError || showCheck) && (
            <div
              className={classes(
                styles.divider,
                showError && styles['divider-error'],
              )}
            />
          )}
          {showError && (
            <ErrorIcon
              className={classes(styles.badge, styles['error-icon'])}
              data-test={`${dataTest}-error-icon`}
            />
          )}
          {showCheck && (
            <CheckIcon
              className={styles.badge}
              data-test={`${dataTest}-check-icon`}
            />
          )}
          {withSearchIcon && (
            <MagnifyingGlassIcon
              className={classes(styles.badge, styles.searchIcon)}
              data-test={`${dataTest}-search-icon`}
            />
          )}
          {isLoading && loadingAnimation}
        </div>
      </div>

      {showError && typeof error !== 'boolean' && (
        <div className={classes(styles['error-message'], errorClassName)}>
          <ErrorMessage errors={error} dataTest={`${dataTest}-error`} />
        </div>
      )}
    </div>
  );
});
